/**
 * AdminSidebar — the Administration area's OWN settings nav (Story UI-U6 AC1,
 * design.md §5 "Settings / Admin exception"): it REPLACES the main app sidebar.
 * "← Zurück zur App" on top, then the grouped sections; each entry is filtered by
 * the caller's effective permissions (`canAny(requiredKeys)`) — the api guards
 * remain the hard line, this is guard-AWARENESS rendered from the server's answer.
 *
 * THE SHARED ADMIN SURFACE (coordination): sibling story UI-U5 extends `adminNav.ts`
 * (Tarifmatrix into the Mandant group) — this component renders whatever ADMIN_NAV
 * contains, so that lands as a pure data addition.
 *
 * [Source: apps/web/src/app/shell/AppShell.tsx] (NavLink/Section patterns)
 * [Source: docs/design/2026-07-10_sebastian-package/design.md §5]
 */

import { Link, useRouterState } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { cn } from '../../lib/cn';
import { ADMIN_NAV, type AdminNavItem } from './adminNav';
import { useEffectivePermissions } from './useEffectivePermissions';

function AdminNavLink({ item, active }: { item: AdminNavItem; active: boolean }) {
  const Icon = item.icon;
  return (
    <Link
      to="/admin/$section"
      params={{ section: item.key }}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
          : 'text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
      )}
    >
      <Icon className={cn('size-4 shrink-0', !active && 'text-sidebar-foreground/60')} />
      <span className="flex-1">{item.label}</span>
    </Link>
  );
}

export function AdminSidebar() {
  const { canAny } = useEffectivePermissions();
  const path = useRouterState({ select: (s) => s.location.pathname });

  // Filter each group's items by access; drop groups that end up empty.
  const groups = ADMIN_NAV.map((g) => ({
    ...g,
    items: g.items.filter((it) => canAny(it.requiredKeys)),
  })).filter((g) => g.items.length > 0);

  return (
    <aside
      aria-label="Administration"
      className="flex w-64 shrink-0 flex-col border-r border-sidebar-border bg-sidebar"
    >
      <div className="px-3 pt-4">
        <Link
          to="/"
          className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/60 hover:text-sidebar-foreground"
        >
          <ArrowLeft className="size-4" />
          <span>Zurück zur App</span>
        </Link>
        <div className="mt-3 px-1.5 pb-1">
          <div className="text-sm font-semibold text-sidebar-foreground">Administration</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-2">
        {groups.map((group, idx) => (
          <div key={group.label} className={idx > 0 ? 'mt-4' : undefined}>
            <div className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/40">
              {group.label}
            </div>
            <div className="space-y-0.5">
              {group.items.map((it) => (
                <AdminNavLink key={it.key} item={it} active={path === `/admin/${it.key}`} />
              ))}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );
}
